<script lang="ts" setup>
import DirectSalesForm from '@/domains/direct-sales/components/direct-sales-form/direct-sales-form.vue'
import { EUiFormColor } from '@/domains/direct-sales/const/form.const'
import { DIRECT_SALES_UTMS } from '@/domains/direct-sales/const/utm.const'

import SolutionsImageWithAccordeon from '@/domains/solutions/solutions-imageWithAccordeon/solutions-imageWithAccordeon.vue'
import SolutionsImageWithText from '@/domains/solutions/solutions-imageWithText/solutions-imageWithText.vue'
import SolutionsImageWithTextAndBoxes from '@/domains/solutions/solutions-imageWithTextAndBoxes/solutions-imageWithTextAndBoxes.vue'
import SolutionsTextWithWhiteBoxes from '@/domains/solutions/solutions-textWithWhiteBoxes/solutions-textWithWhiteBoxes.vue'
import { adaptNumbers } from '@/domains/home/composables/useAdaptNumbers'
import useStaticData from '@/composables/useStaticData'
import { useSolutionsPage } from '@/domains/solutions/composables/useSolutionsPage'
 
const { t, locale } = useI18n()
const { content } = await useSolutionsPage()

const data = computed(() => {
  return {
    content: content.value,
  }
})

useHead({
  title: t('pageTitles.solutions.title'),
  meta: [
    {
      name: 'description',
      content: t('pageTitles.solutions.meta'),
    },
  ],
  link: [
    {
      hid: 'favicon',
      rel: 'icon',
      type: 'image/x-icon',
      href: '/favicon.ico',
    },
    {
      rel: 'canonical',
      href: t(`pageTitles.solutions.canonical.${locale.value}`),
    },
    {
      rel: 'alternate',
      hreflang: 'pl',
      href: t('pageTitles.solutions.canonical.pl'),
    },
    {
      rel: 'alternate',
      hreflang: 'en',
      href: t('pageTitles.solutions.canonical.en'),
    },
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: t('pageTitles.solutions.canonical.pl'),
    },
  ],
})

const { getItems } = useStaticData()
const items = await getItems(['accordeon', 'articles', 'numbers', 'iconList', 'boxes'])

// contentful when he didnt found then it use fallback on polish
if (data.value?.content?.items?.bsNumbers?.numbers) {
  data.value.content.items.bsNumbers.numbers = locale.value === 'en' ? data.value.content.items.bsNumbers.numbers.slice(0, -1) : data.value.content.items.bsNumbers.numbers
}

const { iconsList4, iconsList5, boxes3, boxes4, boxes15, accordeon, articles } = items

</script>

<template>
  <div>
    <Hero
      desktop-image="/images/hero/rozwiazania_d_2024.webp"
      mobile-image="/images/hero/rozwiazania_m_2024.webp"
      :header="$t('solutions.hero.header')"
      :text="$t('solutions.hero.text')"
      :button="{
        text: $t('solutions.hero.buttonText'),
        to: '#formularz',
      }"
    />

    <SolutionsImageWithText
      id="section1"
      layout="default"
      :header="$t('solutions.section1.header')"
      :image="{
        src: '/images/image_guy.jpg',
        width: '1320',
      }"
      :text="$t('solutions.section1.text')"
      :text-second="$t('solutions.section1.textSecond')"
    />

    <SolutionsImageWithText
      id="section2"
      layout="default"
      :header="$t('solutions.section2.header')"
      :image="{
        src: $t('solutions.section2.image'),
        width: '770',
      }"
      :subheader="$t('solutions.section2.subheader')"
    />

    <TextInBox
      id="section3"
      layout="default"
      :box="{
        text: $t('solutions.section3.text'),
        subtext: $t('solutions.section3.subtext'),
        image: '/images/image_one-man.jpg',
      }"
    />

    <TextWithImage
      id="section4"
      layout="layout-3"
      :header="$t('solutions.section4.header')"
      position="left"
      :text="$t('solutions.section4.text')"
      :button="{
        text: $t('solutions.section4.buttonText'),
        to: '#formularz',
      }"
      image="/images/image_8.png"
      :list="iconsList4"
    />

    <SolutionsTextWithWhiteBoxes
      id="section5"
      :header="$t('solutions.section5.header')"
      :text="$t('solutions.section5.text')"
      :text-second="$t('solutions.section5.textSecond')"
      :subtext="$t('solutions.section5.subtext')"
      :numbers="adaptNumbers(data?.content?.items.bsNumbers.numbers)"
      :button="{
        text: $t('solutions.section5.buttonText'),
        to: '/wellbeing',
      }"
    />

    <CardCarousel
      v-if="articles?.length"
      id="section6"
      :header="$t('solutions.section6.header')"
      :subheader="$t('solutions.section6.subheader')"
      :slides="articles"
      :button="{
        text: $t('solutions.section6.buttonText'),
        to: '#formularz',
      }"
      color="gray"
    />

    <TextWithBoxes
      id="section7"
      :header="$t('solutions.section7.header')"
      position="center"
      layout="default"
      :text="$t('solutions.section7.text')"
      :boxes="boxes15"
      :button="{
        text: $t('solutions.section7.buttonText'),
        to: '#formularz',
      }"
      color="gray"
    />

    <TextWithBoxes
      id="section8"
      layout="layout-1"
      :header="$t('solutions.section8.header')"
      position="left"
      :button="{
        text: $t('solutions.section8.buttonText'),
        to: '#formularz',
      }"
      :boxes="boxes3"
    />

    <SolutionsImageWithTextAndBoxes
      id="section9"
      layout="default"
      :header="$t('solutions.section9.header')"
      :text="$t('solutions.section9.text')"
      image="/images/image_flag.png"
      color="purple"
      :button="{
        text: $t('solutions.section9.buttonText'),
        to: '#formularz',
      }"
      :boxes="boxes4"
    />

    <TextWithIconBoxes
      id="section11"
      :header="$t('solutions.section11.header')"
      :text="$t('solutions.section11.text')"
      :text-second="$t('solutions.section11.textSecond')"
      :list="iconsList5"
      color="gray"
      layout="layout-1"
      :button="{
        text: $t('solutions.section11.buttonText'),
        to: '#formularz',
      }"
    />

    <SolutionsImageWithAccordeon
      id="section12"
      :header="$t('solutions.section12.header')"
      :subheader="$t('solutions.section12.subheader')"
      image="/images/accordeon.png"
      :data="accordeon"
    />

    <DirectSalesForm
      :header="$t('solutions.form.header')"
      :color="EUiFormColor.GRAY"
      :utm="DIRECT_SALES_UTMS.solutionsPage"
      :badge="`/icons/badges/flag-4_${locale}.png`"
:form-id="$t('solutions.form.id')"
    />
  </div>
</template>

<style lang="scss" scoped>
  :deep() {
    --span-color: #9C5BEF;
  }
</style>
